/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Assets, CarbDevices, Devices, EntityStatus} from '@ion/core/src/types/entities';
import {EmissionTester} from '@ion/core/src/types/services';
import {isANumber} from '@ion/core/src/utils/NumberUtil';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import Render from '@ion/dls/src/render/Render';
import useSearchAssets from '@ion/query/src/hooks/graphql/useSearchAssets';
import useEmissionTester from '@ion/query/src/hooks/useEmissionTester';
import CarbDevicesTable from './CarbDevicesTable';
import {ComplianceFields} from './ComplianceFields';
import ComplianceForm from './ComplianceForm';

export interface ComplianceFormProps {
  setIsProcessButtonDisabled?: Function;
  selectedIdentifierType?: string;
  isSelectedFromGrid?: boolean;
  selectedRowEntities?: Devices;
  serviceStatus?: EntityStatus;
  setCarbDevices: Function;
  devicesRef: any;
  devices: CarbDevices;
  error?: boolean;
  accountId: number;
};

export const useStyles = makeStyles(() => ({
  spinner : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  },
  disabledContainer : {
    opacity       : 0.5,
    pointerEvents : 'none'
  }
}));

const getAssetDetails = (assets: Assets) => {
  const assetDetails = {};
  assets?.forEach((asset) => {
    if (asset?.vin !== '') {
      assetDetails[asset.vin] = {
        vin          : asset?.vin,
        licensePlate : asset?.licenseNumber,
        licenseState : asset?.licenseState || ''
      };
    }
  });

  return assetDetails;
};

/**
 * The CarbServiceForm component that manages and displays CARB complaince specialist information of current account and
 * CARB devices and assets table.
 * 
 * ```typescript
 * import {CarbServiceForm} from '@ion/applications/src/bulk/carb/CarbServiceForm';
 * 
 * const SomeComponent = () => {
 *   return (
 *     <CarbServiceForm  accounId={accountId} 
 *       ... />
 *   );
 * };
 * ```
 */
export const CarbServiceForm = ({
  isSelectedFromGrid = false,
  setIsProcessButtonDisabled,
  selectedIdentifierType,
  selectedRowEntities,
  serviceStatus,
  devicesRef,
  accountId,
  devices,
  error
}: ComplianceFormProps) => {
  const classes                             = useStyles();
  const [showCompliance, setShowCompliance] = useState(false);
  const [carbDevices, setCarbDevices]       = useState(devices);
  const [showCancel, setShowCancel]         = useState(false);
  const {
    emissionTester,
    isLoading,
    refresh,
    isFetched: isTesterFetched
  } = useEmissionTester({id: accountId}, true);
  const vins                                = devices?.map(({vin}) => vin)?.filter(vin => vin !== '') || [];
  const assetIds                            = selectedRowEntities?.length
    ? selectedRowEntities?.map(({asset}) => entityId(asset))?.filter(id => isANumber(id)) : [];
  const filter = assetIds?.length
    ? {id: assetIds} : {vin: vins};
  const {
    assets,
    isFetched,
    isFetching
  } = useSearchAssets({
    fields  : ['id', 'name', 'licenseNumber', 'licenseState', 'vin'],
    search  : {filter},
    page    : 1,
    perPage : 1000
  }, {enabled: !!vins?.length || !!assetIds?.length, normalize: true, isWithGenerator: true});

  useEffect(() => {
    setShowCompliance(!emissionTester?.testerId);
    setShowCancel?.(!!emissionTester?.testerId);
  }, [isTesterFetched]);

  useEffect(() => {
    if (!showCompliance) {
      refresh();
    }
  }, [showCompliance]);

  const getVin = ({identifier}) => {
    const device = selectedRowEntities?.filter(({id}) => id.toString() === identifier)?.[0];
    const assetId = entityId(device?.asset);
    const vin = assets.filter(({id}) => id === assetId)?.[0]?.vin || '';

    return vin;
  };

  useEffect(() => {
    if (isFetched) {
      let newDevices = [];
      const assetDetails = getAssetDetails(assets);
      newDevices = carbDevices?.map((device) => {
        const vin = assetIds?.length ? getVin(device) : device.vin;
        if (assetDetails?.[vin]) {
          const asstDetails = assetDetails?.[vin];
          return {
            ...device,
            ...asstDetails
          };
        } else {
          return device;
        }
      });
      devicesRef.current = newDevices;
      setCarbDevices(newDevices);
    }
  }, [isFetched]);

  const onClickEdit = useCallback((show) => {
    setShowCompliance(show);
  }, []);
  
  if (isLoading || isFetching) {
    return (<div className={classes.spinner}>
      <IonSpinner width='25px' height='25px' />
    </div>);
  }

  return (
    <div className={serviceStatus===EntityStatus.Disabled ? classes.disabledContainer: ''}>
      <Render.If condition={!showCompliance}>
        <ComplianceFields complianceValues={emissionTester}
          onEdit={onClickEdit} />
      </Render.If>
      <Render.If condition={showCompliance}>
        <ComplianceForm showCancel={showCancel}
          setShowCancel={setShowCancel}
          complianceValues={emissionTester}
          setShowCompliance={setShowCompliance} />
      </Render.If>
      <Render.If condition={!showCompliance}>
        <CarbDevicesTable error={error}
          selectedRowEntities={selectedRowEntities}
          isSelectedFromGrid={isSelectedFromGrid}
          setIsProcessButtonDisabled={setIsProcessButtonDisabled}
          devices={carbDevices}
          serviceStatus={serviceStatus}
          setCarbDevices={setCarbDevices}
          devicesRef={devicesRef}
          selectedIdentifierType={selectedIdentifierType} />
      </Render.If>
    </div>
  );
};

export default CarbServiceForm;
